import React from 'react';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiCheck, FiChevronDown } from 'react-icons/fi';
import styled, { createGlobalStyle } from 'styled-components';

// Styled component for image overlay
const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 50%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 20px;
  border-radius: 8px;
  transform: translateY(100%);
  opacity: 0;
  transition: transform 0.3s ease, opacity 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  overflow-y: auto;

  h4 {
    margin: 0 0 10px;
    font-size: 18px;
    color: var(--theme-color);
  }

  ul {
    list-style-type: none;
    padding: 0;
    margin: 0;
  }

  li {
    margin-bottom: 8px;
    position: relative;
    padding-left: 20px;
  }

  li:before {
    content: "•";
    color: var(--theme-color);
    position: absolute;
    left: 0;
    font-weight: bold;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  margin: 20px 0;

  &:hover ${ImageOverlay} {
    transform: translateY(0);
    opacity: 1;
  }

  .content-image {
    display: block;
    width: 100%;
    height: auto;
  }
`;

// Global Styles
const GlobalStyles = createGlobalStyle`
  :root {
    --theme-color: #f7bf68;
  }
  /* Insurance Content Section */
  .insurance-content-section {
    padding: 80px 0;
    background-color: #f9f9f9;
  }

  .insurance-tabs {
    display: flex;
    margin-bottom: 40px;
    border-bottom: 1px solid #ddd;
    flex-wrap: wrap;
  }

  .tab-btn {
    padding: 12px 24px;
    margin-right: 10px;
    background: none;
    border: none;
    border-bottom: 3px solid transparent;
    font-size: 16px;
    font-weight: 600;
    color: #666;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .tab-btn:hover,
  .tab-btn.active {
    color: var(--theme-color);
    border-bottom-color: var(--theme-color);
  }

  .content-section {
    background: white;
    padding: 40px;
    border-radius: 8px;
    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
    margin-bottom: 30px;
  }

  .section-header {
    margin-bottom: 30px;
    text-align: center;
  }

  .section-header h2 {
    color: var(--theme-color);
    margin-bottom: 10px;
  }

  .subtitle {
    color: #666;
    font-size: 18px;
  }

  .content-box {
    margin-bottom: 30px;
  }

  .highlight-box {
    background-color: #fff8ec;
    padding: 25px;
    border-radius: 8px;
    border-left: 4px solid var(--theme-color);
  }

  /* Tables */
  .styled-table {
    width: 100%;
    border-collapse: collapse;
    margin: 20px 0;
    font-size: 0.9em;
    min-width: 400px;
    border-radius: 5px 5px 0 0;
    overflow: hidden;
    box-shadow: 0 0 20px rgba(0,0,0,0.05);
  }

  .styled-table thead tr {
    background-color: var(--theme-color);
    color: #ffffff;
    text-align: left;
    font-weight: bold;
  }

  .styled-table th,
  .styled-table td {
    padding: 12px 15px;
  }

  .styled-table tbody tr {
    border-bottom: 1px solid #dddddd;
  }

  .styled-table tbody tr:nth-of-type(even) {
    background-color: #f3f3f3;
  }

  .styled-table tbody tr:last-of-type {
    border-bottom: 2px solid var(--theme-color);
  }

  .styled-table tbody tr.active-row {
    font-weight: bold;
    color: var(--theme-color);
  }

  .expand-table-btn {
    background: none;
    border: none;
    color: var(--theme-color);
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    margin: 10px 0;
  }

  .expand-table-btn svg {
    margin-left: 5px;
    transition: transform 0.3s ease;
  }

  .expand-table-btn .rotate {
    transform: rotate(180deg);
  }

  .table-responsive {
    max-height: 400px;
    overflow: hidden;
    transition: max-height 0.5s ease;
  }

  .table-responsive.expanded {
    max-height: none;
  }

  /* Lists */
  .styled-list {
    list-style-type: none;
    padding-left: 0;
  }

  .styled-list li {
    position: relative;
    padding-left: 25px;
    margin-bottom: 10px;
    line-height: 1.6;
  }

  .styled-list li:before {
    content: "•";
    color: var(--theme-color);
    font-weight: bold;
    position: absolute;
    left: 0;
  }

  /* Checklist */
  .checklist {
    margin: 20px 0;
  }

  .check-item {
    display: flex;
    align-items: center;
    margin-bottom: 5px;
  }

  .check-icon {
    color: var(--theme-color);
    margin-right: 10px;
    font-size: 18px;
  }

  /* Cards */
  .provider-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
    margin: 30px 0;
  }

  .provider-card {
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
    border-top: 3px solid var(--theme-color);
  }

  .provider-card h3 {
    color: var(--theme-color);
    margin-top: 0;
  }

  .scenario-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
    margin: 20px 0;
  }

  .scenario-card {
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
    border-left: 3px solid var(--theme-color);
  }

  .insurance-types {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
    margin: 20px 0;
  }

  .type-card {
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
    border: 1px solid #eee;
  }

  .type-card h4 {
    color: var(--theme-color);
    margin-top: 0;
  }

  /* Pros and Cons */
  .pros-cons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    margin: 20px 0;
  }

  .pros {
    background: #fff8ec;
    padding: 20px;
    border-radius: 8px;
    border-left: 4px solid var(--theme-color);
  }

  .cons {
    background: #fff8ec;
    padding: 20px;
    border-radius: 8px;
    border-left: 4px solid var(--theme-color);
  }

  .pros h4 {
    color: var(--theme-color);
    margin-top: 0;
  }

  .cons h4 {
    color: var(--theme-color);
    margin-top: 0;
  }

  /* Footnotes */
  .footnote {
    font-size: 0.85em;
    color: #666;
    font-style: italic;
    margin-top: 5px;
  }

  /* Image Styling */
  .content-image {
    max-width: 50%;
    height: auto;
    border-radius: 8px;
    margin: 20px 0;
    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
  }

  /* Responsive */
  @media (max-width: 768px) {
    .insurance-tabs {
      flex-direction: column;
    }
    
    .tab-btn {
      margin-bottom: 10px;
      text-align: left;
    }
    
    .pros-cons {
      grid-template-columns: 1fr;
    }
    
    .content-section {
      padding: 20px;
    }

    .content-image {
      max-width: 100%;
    }
  }
`;

export default function StartUpBusinessGrowth() {
    const [isLoading, setIsLoading] = useState(true);
    const [activeSection, setActiveSection] = useState('trends-2026');
    const [expandedTables, setExpandedTables] = useState({});

    useEffect(() => {
        setTimeout(() => {
            setIsLoading(false)
        }, 2000);
    }, []);

    const toggleTableExpand = (tableId) => {
        setExpandedTables(prev => ({
            ...prev,
            [tableId]: !prev[tableId]
        }));
    };

    const fadeIn = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    const sectionVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
    };

    return (
        <div>
            <GlobalStyles />
            <div className="boxed_wrapper ltr">
                {isLoading && <div className="loader-wrap">
                    <div className="preloader">
                        <div className="preloader-close">close</div>
                        <div id="handle-preloader" className="handle-preloader">
                            <div className="animation-preloader">
                                <div className="txt-loading">
                                    <img src="assets/images/animation_logo.gif" alt="img" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>}

                <Header />

                {/* page-title */}
                <section className="page-title">
                    <div
                        className="bg-layer"
                        style={{
                            backgroundImage: `url("/assets/images/background/page-title.jpg")`,
                        }}
                    ></div>
                    <div className="line-grid-wrap">
                        <span className="line-grid-1"></span>
                        <span className="line-grid-2"></span>
                        <span className="line-grid-3"></span>
                        <span className="line-grid-4"></span>
                        <span className="line-grid-5"></span>
                    </div>
                    <div className="auto-container">
                        <div className="content-box">
                            <h1 style={{ textAlign: "left" }}>Startup & Business Growth</h1>
                            <ul className="bread-crumb clearfix">
                                <li>
                                    <Link to="/">Home</Link>
                                </li>
                                <li>Startup & Business Growth</li>
                            </ul>
                        </div>
                    </div>
                </section>
                {/* Page Title End */}

                {/* Main Content Section */}
                <section className="insurance-content-section">
                    <div className="auto-container">
                        {/* Navigation Tabs */}
                        <motion.div 
                            className="insurance-tabs"
                            initial="hidden"
                            animate="visible"
                            variants={fadeIn}
                        >
                            <button 
                                className={`tab-btn ${activeSection === 'trends-2026' ? 'active' : ''}`}
                                onClick={() => setActiveSection('trends-2026')}
                            >
                                Business Trends 2026
                            </button>
                            <button 
                                className={`tab-btn ${activeSection === 'trends' ? 'active' : ''}`}
                                onClick={() => setActiveSection('trends')}
                            >
                                Business Trends
                            </button>
                            <button 
                                className={`tab-btn ${activeSection === 'banking' ? 'active' : ''}`}
                                onClick={() => setActiveSection('banking')}
                            >
                                Banking Relationships
                            </button>
                            <button 
                                className={`tab-btn ${activeSection === 'lending' ? 'active' : ''}`}
                                onClick={() => setActiveSection('lending')}
                            >
                                Business Lending
                            </button>
                            <button 
                                className={`tab-btn ${activeSection === 'planning' ? 'active' : ''}`}
                                onClick={() => setActiveSection('planning')}
                            >
                                Business Planning
                            </button>
                        </motion.div>

                        {/* Business Trends 2026 Section - Small Business & Economic Trends */}
                        {activeSection === 'trends-2026' && (
                            <motion.div 
                                className="content-section"
                                initial="hidden"
                                animate="visible"
                                variants={sectionVariants}
                            >
                                <motion.div variants={fadeIn} className="section-header">
                                    <h2>Small Business & Economic Trends 2026</h2>
                                    <p className="subtitle">Outlook, opportunities, and strategies for the year ahead</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>2026 Economic Outlook for Small Business</h3>
                                    <p>The landscape for small businesses in 2026 is shaped by interest rates, labor markets, technology adoption, and policy. Understanding these trends helps you plan funding, hiring, and growth with confidence.</p>
                                    <div className="scenario-cards">
                                        <div className="scenario-card">
                                            <h4>Interest Rates & Financing</h4>
                                            <p>Rates are expected to stabilize from recent highs. Lock in favorable terms where possible and keep business credit strong for refinancing options.</p>
                                        </div>
                                        <div className="scenario-card">
                                            <h4>Labor & Talent</h4>
                                            <p>Competition for skilled workers remains strong. Focus on retention, training, and flexible work to attract and keep talent.</p>
                                        </div>
                                        <div className="scenario-card">
                                            <h4>Technology & Efficiency</h4>
                                            <p>AI and automation continue to reduce costs and improve operations. Small businesses that adopt tools early gain a lasting edge.</p>
                                        </div>
                                        <div className="scenario-card">
                                            <h4>Regulation & Compliance</h4>
                                            <p>Stay informed on federal and state changes affecting taxes, labor, and industry-specific rules to avoid surprises.</p>
                                        </div>
                                    </div>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/business-trends-2026-1.avif" 
                                            alt="Small business and economic outlook 2026" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>2026 Outlook at a Glance</h4>
                                            <ul>
                                                <li>Financing: Rates stabilizing; keep credit strong.</li>
                                                <li>Labor: Compete with retention and flexibility.</li>
                                                <li>Technology: Adopt AI and automation early.</li>
                                                <li>Compliance: Track regulatory changes.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Key Small Business Metrics to Watch in 2026</h3>
                                    <p>Tracking optimism, hiring plans, capital spending, and credit conditions helps you align your strategy with the broader market.</p>
                                    <div className={`table-responsive ${expandedTables['metrics2026'] ? 'expanded' : ''}`}>
                                        <table className="styled-table">
                                            <thead>
                                                <tr>
                                                    <th>Metric</th>
                                                    <th>What It Means</th>
                                                    <th>Action for You</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>Small Business Optimism Index</td>
                                                    <td>Overall confidence in economy and growth</td>
                                                    <td>Plan expansion when index trends up; conserve when it dips.</td>
                                                </tr>
                                                <tr>
                                                    <td>Plans to Increase Employment</td>
                                                    <td>Hiring intentions</td>
                                                    <td>Time hiring with labor availability and wage trends.</td>
                                                </tr>
                                                <tr>
                                                    <td>Capital Outlay Plans</td>
                                                    <td>Investment in equipment and facilities</td>
                                                    <td>Consider upgrades when financing is favorable.</td>
                                                </tr>
                                                <tr>
                                                    <td>Expected Credit Conditions</td>
                                                    <td>Ease or tightness of lending</td>
                                                    <td>Build banking relationships and credit before you need funds.</td>
                                                </tr>
                                                <tr>
                                                    <td>Earnings Trends</td>
                                                    <td>Net % reporting higher earnings</td>
                                                    <td>Benchmark your margins and pricing against sector norms.</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <button 
                                        className="expand-table-btn" 
                                        onClick={() => toggleTableExpand('metrics2026')}
                                    >
                                        {expandedTables['metrics2026'] ? 'Show Less' : 'Show More'} <FiChevronDown className={expandedTables['metrics2026'] ? 'rotate' : ''} />
                                    </button>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/business-trends-2026-2.jpg" 
                                            alt="Key small business metrics 2026" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>Metrics That Matter</h4>
                                            <ul>
                                                <li>Optimism Index: Gauge overall confidence.</li>
                                                <li>Employment plans: Align with labor market.</li>
                                                <li>Capital outlays: Plan when to invest.</li>
                                                <li>Credit conditions: Prepare before borrowing.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Top Challenges & How to Address Them</h3>
                                    <p>Small business owners consistently cite labor quality, taxes, inflation, and regulation. Here’s how to stay ahead.</p>
                                    <div className="provider-cards">
                                        <div className="provider-card">
                                            <h4>Labor Quality</h4>
                                            <p>Invest in training, competitive pay, and benefits. Use flexible schedules and remote options where possible to widen your talent pool.</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Taxes</h4>
                                            <p>Work with a CPA or advisor to maximize deductions, structure, and timing. Plan for estimated taxes and policy changes.</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Inflation & Cost of Goods</h4>
                                            <p>Lock in supplier contracts, streamline inventory, and consider selective price increases with clear value messaging.</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Regulations & Insurance</h4>
                                            <p>Stay compliant with labor and industry rules. Shop insurance annually and bundle where possible to control costs.</p>
                                        </div>
                                    </div>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/business-trends-2026-3.jpg" 
                                            alt="Small business challenges and solutions 2026" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>Staying Ahead of Challenges</h4>
                                            <ul>
                                                <li>Labor: Train, pay well, offer flexibility.</li>
                                                <li>Taxes: Plan with a qualified advisor.</li>
                                                <li>Inflation: Manage suppliers and pricing.</li>
                                                <li>Regulation: Comply and review insurance.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Ravline Corp: Your Partner for 2026</h3>
                                    <div className="checklist">
                                        <div className="check-item">
                                            <FiCheck className="check-icon" />
                                            <span><strong>How we support small business growth:</strong></span>
                                        </div>
                                        <ul className="styled-list">
                                            <li>Business credit building and monitoring</li>
                                            <li>Banking and lending strategy guidance</li>
                                            <li>Financial planning aligned with economic trends</li>
                                            <li>Connections to funding and resources</li>
                                        </ul>
                                    </div>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/business-trends-2026-4.jpg" 
                                            alt="Ravline Corp small business support 2026" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>Ravline Supports Your 2026 Goals</h4>
                                            <ul>
                                                <li>Build and monitor business credit.</li>
                                                <li>Navigate banking and lending options.</li>
                                                <li>Plan with current trends in mind.</li>
                                                <li>Access funding and growth resources.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box highlight-box">
                                    <h3>Get in Touch</h3>
                                    <p>Ravline Corp helps small businesses prepare for 2026 with credit, banking, and planning support. Reach out to start the conversation.</p>
                                    <div className="provider-cards">
                                        <div className="provider-card">
                                            <h4>Email</h4>
                                            <p><a href="mailto:ravlinecorp@gmail.com">ravlinecorp@gmail.com</a><br /><a href="mailto:rodneyrutty@ravlinecorp.com">rodneyrutty@ravlinecorp.com</a></p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Phone</h4>
                                            <p>954-709-4029<br />305-970-7421</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Website</h4>
                                            <p><a href="https://www.ravlinecorp.com" target="_blank" rel="noopener noreferrer">www.ravlinecorp.com</a></p>
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}

                        {/* Business Trends Section */}
                        {activeSection === 'trends' && (
                            <motion.div 
                                className="content-section"
                                initial="hidden"
                                animate="visible"
                                variants={sectionVariants}
                            >
                                <motion.div variants={fadeIn} className="section-header">
                                    <h2>Small Business Trends Report — April 2025</h2>
                                    <p className="subtitle">Based on NFIB Economic Survey Insights</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Overview</h3>
                                    <p>The <strong>NFIB Small Business Optimism Index</strong> fell to <strong>95.8</strong> in April 2025 — a dip below the 51-year average of 98. Small business owners across the U.S. report concern about inflation, labor quality, taxes, and economic uncertainty. Despite challenges, business owners remain resilient, showing signs of recovery in compensation and earnings.</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Key Economic Trends (April 2025)</h3>
                                    <div className={`table-responsive ${expandedTables['economicTrends'] ? 'expanded' : ''}`}>
                                        <table className="styled-table">
                                            <thead>
                                                <tr>
                                                    <th>Index Component</th>
                                                    <th>April 2025</th>
                                                    <th>Change from March</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>Plans to Increase Employment</td>
                                                    <td>13%</td>
                                                    <td>▲ +1</td>
                                                </tr>
                                                <tr>
                                                    <td>Plans for Capital Outlays</td>
                                                    <td>18%</td>
                                                    <td>▼ -3</td>
                                                </tr>
                                                <tr>
                                                    <td>Expect Economy to Improve</td>
                                                    <td>15%</td>
                                                    <td>▼ -6</td>
                                                </tr>
                                                <tr>
                                                    <td>Expect Real Sales to Increase</td>
                                                    <td>-1%</td>
                                                    <td>▼ -4</td>
                                                </tr>
                                                <tr>
                                                    <td>Current Job Openings</td>
                                                    <td>34%</td>
                                                    <td>▼ -6</td>
                                                </tr>
                                                <tr>
                                                    <td>Expected Credit Conditions</td>
                                                    <td>-7%</td>
                                                    <td>▼ -3</td>
                                                </tr>
                                                <tr>
                                                    <td>Earnings Trends (net)</td>
                                                    <td>-21%</td>
                                                    <td>▲ +7</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <button 
                                        className="expand-table-btn" 
                                        onClick={() => toggleTableExpand('economicTrends')}
                                    >
                                        {expandedTables['economicTrends'] ? 'Show Less' : 'Show More'} <FiChevronDown className={expandedTables['economicTrends'] ? 'rotate' : ''} />
                                    </button>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/SUB1.jpg" 
                                            alt="Economic trends chart" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>Key Economic Trends</h4>
                                            <ul>
                                                <li>Optimism Index dropped to 95.8 in April 2025.</li>
                                                <li>Employment plans up to 13%, capital outlays down to 18%.</li>
                                                <li>Economic improvement expectations fell to 15%.</li>
                                                <li>Earnings trends improved to -21% net.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Labor Market Challenges</h3>
                                    <ul className="styled-list">
                                        <li><strong>47%</strong> of business owners hiring or trying to hire report <strong>few or no qualified applicants</strong>.</li>
                                        <li><strong>19%</strong> still cite <strong>labor quality</strong> as the <strong>#1 issue</strong>.</li>
                                        <li>Job openings are particularly hard to fill in construction, transportation, and manufacturing.</li>
                                    </ul>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Capital Investment & Equipment</h3>
                                    <ul className="styled-list">
                                        <li><strong>58%</strong> of small businesses made capital outlays in the last six months.</li>
                                        <li>Of those:
                                            <ul className="styled-list">
                                                <li>40% bought new equipment</li>
                                                <li>25% acquired vehicles</li>
                                                <li>16% expanded facilities</li>
                                            </ul>
                                        </li>
                                        <li>But: Only <strong>18% plan future capital spending</strong>, the lowest since April 2020.</li>
                                    </ul>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Sales & Inventory</h3>
                                    <ul className="styled-list">
                                        <li><strong>8%</strong> more businesses reported higher sales in the last three months.</li>
                                        <li>However, <strong>real sales expectations dropped</strong> to a net -1%.</li>
                                        <li><strong>Inventory plans</strong> declined to net -4%, signaling conservative forecasting.</li>
                                    </ul>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box highlight-box">
                                    <h3>Top Challenges Facing Small Businesses</h3>
                                    <div className={`table-responsive ${expandedTables['challenges'] ? 'expanded' : ''}`}>
                                        <table className="styled-table">
                                            <thead>
                                                <tr>
                                                    <th>Top Challenges (April 2025)</th>
                                                    <th>% of Business Owners</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>Labor Quality</td>
                                                    <td>19%</td>
                                                </tr>
                                                <tr>
                                                    <td>Taxes</td>
                                                    <td>16%</td>
                                                </tr>
                                                <tr>
                                                    <td>Inflation (Costs of Goods)</td>
                                                    <td>14%</td>
                                                </tr>
                                                <tr>
                                                    <td>Cost of Insurance</td>
                                                    <td>10%</td>
                                                </tr>
                                                <tr>
                                                    <td>Government Regulations</td>
                                                    <td>9%</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <button 
                                        className="expand-table-btn" 
                                        onClick={() => toggleTableExpand('challenges')}
                                    >
                                        {expandedTables['challenges'] ? 'Show Less' : 'Show More'} <FiChevronDown className={expandedTables['challenges'] ? 'rotate' : ''} />
                                    </button>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/SUB2.jpg" 
                                            alt="Small business challenges" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>Top Business Challenges</h4>
                                            <ul>
                                                <li>Labor quality tops at 19%.</li>
                                                <li>Taxes affect 16% of owners.</li>
                                                <li>Inflation impacts 14%.</li>
                                                <li>Insurance costs concern 10%.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>
                            </motion.div>
                        )}

                        {/* Banking Relationships Section */}
                        {activeSection === 'banking' && (
                            <motion.div 
                                className="content-section"
                                initial="hidden"
                                animate="visible"
                                variants={sectionVariants}
                            >
                                <motion.div variants={fadeIn} className="section-header">
                                    <h2>Choosing the Right Banking Relationship for Your Business</h2>
                                    <p className="subtitle">Not all banks are created equal - especially when it comes to supporting small businesses</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <p>Establishing the right banking relationship is a cornerstone for business success, funding, and operational efficiency.</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Types of Banks & Lending Institutions</h3>
                                    <div className="provider-cards">
                                        <div className="provider-card">
                                            <h4>Commercial Banks</h4>
                                            <p>National or regional institutions (e.g., Bank of America, Chase) that offer a full suite of lending and cash management services. Good for established businesses.</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Credit Unions</h4>
                                            <p>Member-owned with lower fees and community focus. Can be slower to fund but great for local access.</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Community Banks</h4>
                                            <p>Focused on small business and local lending. Often more flexible with underwriting decisions.</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Online Banks & Fintech Lenders</h4>
                                            <p>Offer fast approvals, minimal paperwork, and competitive rates. Ideal for startups or fast-moving ventures.</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>SBA-Approved Lenders</h4>
                                            <p>Specialize in government-backed loans. Banks that frequently work with SBA loans understand the process better and offer more support.</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>CDFIs</h4>
                                            <p>Community Development Financial Institutions focused on underserved businesses or areas. These can provide grants, microloans, and mentorship along with funding.</p>
                                        </div>
                                    </div>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/SUB3.jpg" 
                                            alt="Types of banking institutions" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>Banking Benefits</h4>
                                            <ul>
                                                <li>Commercial banks offer full services for established firms.</li>
                                                <li>Credit unions provide lower fees and local support.</li>
                                                <li>Community banks offer flexible lending terms.</li>
                                                <li>Fintechs deliver fast funding for startups.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Why Building a Relationship Matters</h3>
                                    <p>When lenders know your business and trust your operations, you gain access to:</p>
                                    <ul className="styled-list">
                                        <li>Faster loan approvals</li>
                                        <li>Higher lending limits</li>
                                        <li>Lower interest rates</li>
                                        <li>Business line of credit</li>
                                        <li>Flexible terms in tough times</li>
                                    </ul>
                                    <p>A trusted banking relationship is a strategic financial asset.</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>How to Develop That Relationship</h3>
                                    <ul className="styled-list">
                                        <li>Open a business checking and savings account</li>
                                        <li>Assign a dedicated banker or account manager</li>
                                        <li>Schedule quarterly reviews of your financials</li>
                                        <li>Keep your books and tax filings up-to-date</li>
                                        <li>Apply for small lending products early (like credit cards or secured lines)</li>
                                        <li>Ask for advice - good bankers are advisors too</li>
                                    </ul>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box highlight-box">
                                    <h3>Best Fits for Small Businesses</h3>
                                    <div className="provider-cards">
                                        <div className="provider-card">
                                            <h4>Chase for Business</h4>
                                            <p>Excellent integrations, credit lines, and online tools</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Bank of America</h4>
                                            <p>Cash flow monitoring and access to preferred SBA programs</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Lendio / Bluevine / Novo</h4>
                                            <p>Fintech solutions for fast funding with low minimums</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Wells Fargo</h4>
                                            <p>Great for equipment financing and startup lines</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Local Community Banks</h4>
                                            <p>Personalized service and flexible terms</p>
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}

                        {/* Business Lending Section */}
                        {activeSection === 'lending' && (
                            <motion.div 
                                className="content-section"
                                initial="hidden"
                                animate="visible"
                                variants={sectionVariants}
                            >
                                <motion.div variants={fadeIn} className="section-header">
                                    <h2>Understanding Business Lending & How to Get Approved</h2>
                                    <p className="subtitle">Access to business funding is critical for growth, expansion, and working capital</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <p>Understanding the types of business loans—and how to qualify—is the first step to scaling a successful company.</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Types of Business Lending Products</h3>
                                    <div className="provider-cards">
                                        <div className="provider-card">
                                            <h4>Term Loans</h4>
                                            <p>Fixed repayment loan used for working capital, expansion, or purchases.</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Commercial Real Estate Loans</h4>
                                            <p>Long-term financing for buildings or facilities.</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Equipment Financing</h4>
                                            <p>Secured loan for machines, vehicles, or technology.</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Invoice Financing / Factoring</h4>
                                            <p>Short-term cash advances based on unpaid invoices.</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Merchant Cash Advances</h4>
                                            <p>Funding based on daily credit card sales (high cost).</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Business Lines of Credit</h4>
                                            <p>Flexible, revolving access to capital—only pay interest on what you use.</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Business Credit Cards</h4>
                                            <p>Used for everyday expenses, rewards, and building business credit.</p>
                                        </div>
                                    </div>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/SUB4.png" 
                                            alt="Business lending products" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>Lending Options</h4>
                                            <ul>
                                                <li>Term loans for working capital or purchases.</li>
                                                <li>Real estate loans for long-term assets.</li>
                                                <li>Equipment financing for machinery.</li>
                                                <li>Invoice financing for quick cash flow.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Best Practices for Loan & Credit Approval</h3>
                                    <ul className="styled-list">
                                        <li>Work with your <strong>business consultant</strong> to assess capital needs</li>
                                        <li>Involve your <strong>accountant</strong> to prepare accurate financials</li>
                                        <li>Build your <strong>business credit profile</strong> (D&B, NAV, Experian Biz)</li>
                                        <li>Maintain strong personal credit (FICO 680+ preferred)</li>
                                        <li>Separate personal and business finances</li>
                                        <li>Use a formal business email, domain, address, and phone</li>
                                    </ul>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Documents Required by Most Lenders</h3>
                                    <ul className="styled-list">
                                        <li>Business formation documents (EIN, articles, license)</li>
                                        <li>YTD financial statements</li>
                                        <li>2-3 years business tax returns</li>
                                        <li>Bank statements (3-6 months)</li>
                                        <li>Debt schedule</li>
                                        <li>Personal financial statement</li>
                                        <li>Business plan (for startups or large loans)</li>
                                    </ul>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box highlight-box">
                                    <h3>Key Relationships to Build</h3>
                                    <div className="provider-cards">
                                        <div className="provider-card">
                                            <h4>Accountant</h4>
                                            <p>Helps forecast cash flow and clean books</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Banker</h4>
                                            <p>Provides strategic capital and early product access</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Business Consultant</h4>
                                            <p>Structures the company to be funding-ready</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Legal Advisor</h4>
                                            <p>Reviews contracts and personal guarantees</p>
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}

                        {/* Business Planning Section */}
                        {activeSection === 'planning' && (
                            <motion.div 
                                className="content-section"
                                initial="hidden"
                                animate="visible"
                                variants={sectionVariants}
                            >
                                <motion.div variants={fadeIn} className="section-header">
                                    <h2>Understanding Business Plans & NAICS Codes</h2>
                                    <p className="subtitle">A business plan is a blueprint for success</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>What Is a Business Plan?</h3>
                                    <p>A business plan is a structured document that explains your business model, goals, and how you plan to achieve them. It's essential for attracting investors, securing loans, guiding operations, and measuring progress.</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Why Is a Business Plan Important?</h3>
                                    <ul className="styled-list">
                                        <li>Clarifies vision and mission</li>
                                        <li>Supports funding and lending applications</li>
                                        <li>Identifies potential obstacles and solutions</li>
                                        <li>Aligns team around strategic goals</li>
                                        <li>Increases business credibility</li>
                                    </ul>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Core Components of a Business Plan</h3>
                                    <ul className="styled-list">
                                        <li>Executive Summary</li>
                                        <li>Business Description and Model</li>
                                        <li>Market Analysis</li>
                                        <li>Marketing and Sales Strategy</li>
                                        <li>Organizational Structure</li>
                                        <li>Product/Service Description</li>
                                        <li>Operations Plan</li>
                                        <li>Financial Projections</li>
                                        <li>Appendix (resumes, permits, contracts, etc.)</li>
                                    </ul>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/SUB5.jpg" 
                                            alt="Business plan components" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>Business Plan Components</h4>
                                            <ul>
                                                <li>Executive Summary outlines goals.</li>
                                                <li>Market Analysis identifies opportunities.</li>
                                                <li>Financial Projections guide funding.</li>
                                                <li>Operations Plan details execution.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>What Is the NAICS Association?</h3>
                                    <p>NAICS stands for North American Industry Classification System. The NAICS Association maintains a standardized 6-digit code system that categorizes every type of business in the U.S., Canada, and Mexico. These codes are used by:</p>
                                    <ul className="styled-list">
                                        <li>Banks (for loan risk underwriting)</li>
                                        <li>The IRS (for audit flagging and deductions)</li>
                                        <li>Government contracts and grant eligibility</li>
                                    </ul>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box highlight-box">
                                    <h3>Choosing the Right NAICS Code</h3>
                                    <p>Selecting an accurate NAICS code is crucial. For example:</p>
                                    <ul className="styled-list">
                                        <li>541611: Administrative and General Management Consulting</li>
                                        <li>722513: Limited-Service Restaurants</li>
                                    </ul>
                                    <p>Choosing a high-risk or inaccurate code can:</p>
                                    <ul className="styled-list">
                                        <li>Increase insurance or lending costs</li>
                                        <li>Trigger IRS audits</li>
                                        <li>Limit funding opportunities</li>
                                    </ul>
                                </motion.div>
                            </motion.div>
                        )}
                    </div>
                </section>

                <Footer />
            </div>
        </div>
    );
}